/**
 * MyCertificate — Foundation Tier evaluation + badge collection + claim flow.
 *
 * Honest-date principle: criteria not yet met are shown as "—" in calm gray,
 * not red X. Methodology is a path, not a test you fail.
 *
 * Hold #5: calm. The eligibility copy says "Keep walking" not "Try harder."
 */
import { useEffect, useState } from 'react';
import { Award, CheckCircle2, Circle, Loader2, ExternalLink, Download, Trophy } from 'lucide-react';
import api from '../lib/api';
import { apiBase } from '../lib/absoluteMediaUrl';
import EraCertificates from './EraCertificates';
import CertPremiumActions from './CertPremiumActions';

export default function MyCertificate() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [msg, setMsg] = useState('');

  const reload = async () => {
    setBusy(true);
    try {
      const r = await api.get('/certificates/me');
      setData(r.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not load certificate state.');
    }
    setBusy(false);
  };
  useEffect(() => { reload(); }, []);

  const claim = async () => {
    setClaiming(true);
    setMsg('');
    try {
      const r = await api.post('/certificates/claim');
      if (r.data?.issued || r.data?.certificate_id) {
        setMsg('Foundation Tier claimed.');
        await reload();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setMsg(typeof detail === 'object' ? detail.message : (detail || 'Claim failed.'));
    }
    setClaiming(false);
  };

  if (busy && !data) {
    return (
      <div className="p-6"><Loader2 size={20} className="animate-spin text-[#2F8A92]" /></div>
    );
  }
  if (!data) return null;

  const ev = data.evaluation || {};
  const active = data.active_certificate;
  const badges = data.badges || [];
  const verifyUrl = active ? `${window.location.origin}${active.verify_url_path}` : '';
  const pdfUrl = active ? `${apiBase()}/api/certificates/${active.certificate_id}/pdf` : '';

  return (
    <div className="px-5 md:px-10 lg:px-16 py-8 md:py-12 max-w-6xl mx-auto w-full" data-testid="my-certificate-page">
      <div className="flex items-center gap-2 mb-2">
        <Award size={16} className="text-[#D4A84A]" />
        <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">Methodology Certificate</span>
      </div>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-1">
        Foundation Tier
      </h2>
      <p className="font-mono text-xs md:text-sm text-white/65 mb-5">
        Demonstrated engagement with sources, contradictions, and chains. {ev.met_count}/{ev.total} criteria met.
      </p>

      {/* Active certificate */}
      {active && (
        <div className="border border-[#2F8A92]/30 rounded p-4 md:p-5 mb-6 bg-[#2F8A92]/5" data-testid="cert-active-card">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={18} className="text-[#2F8A92]" />
            <span className="font-historical text-xl md:text-2xl text-[#2F8A92] font-light">Foundation Tier — Issued</span>
          </div>
          <p className="font-mono text-xs md:text-sm text-white/85 leading-relaxed">
            Issued {new Date(active.issued_at).toLocaleDateString()} · Public verifiable.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={pdfUrl}
              target="_blank" rel="noopener noreferrer"
              data-testid="cert-pdf-link"
              className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-2 hover:bg-[#D4A84A]/85 inline-flex items-center gap-1.5"
            >
              <Download size={12} /> DOWNLOAD PDF
            </a>
            <a
              href={verifyUrl}
              target="_blank" rel="noopener noreferrer"
              data-testid="cert-verify-link"
              className="font-hud text-xs tracking-widest text-white/85 border border-[#2F8A92]/40 px-3 py-2 hover:text-[#2F8A92] inline-flex items-center gap-1.5"
            >
              <ExternalLink size={12} /> VERIFY URL
            </a>
          </div>
          <CertPremiumActions certificate={active} testIdPrefix="cert-foundation" />
        </div>
      )}

      {/* Criteria checklist */}
      <div className="border border-[#D4A84A]/15 rounded p-4 md:p-5 mb-6">
        <div className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase mb-3">Criteria</div>
        <ul className="space-y-2">
          {(ev.criteria_definitions || []).map((d) => {
            const met = !!ev.criteria?.[d.key];
            return (
              <li
                key={d.key}
                className="flex items-start gap-3"
                data-testid={`cert-criterion-${d.key}`}
              >
                {met ? (
                  <CheckCircle2 size={15} className="text-[#2F8A92] mt-0.5 flex-shrink-0" />
                ) : (
                  <Circle size={15} className="text-white/25 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`font-mono text-xs md:text-sm ${met ? 'text-white' : 'text-white/55'}`}>{d.label}</div>
                </div>
                <div className={`font-hud text-[9px] tracking-widest ${met ? 'text-[#2F8A92]' : 'text-white/35'} uppercase`}>
                  {met ? 'Met' : '—'}
                </div>
              </li>
            );
          })}
        </ul>

        {!active && ev.ready_to_claim && (
          <button
            onClick={claim}
            disabled={claiming}
            data-testid="cert-claim-btn"
            className="mt-5 font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {claiming ? <Loader2 size={13} className="animate-spin" /> : <Award size={13} />}
            CLAIM FOUNDATION TIER
          </button>
        )}
        {!active && !ev.ready_to_claim && (
          <p className="mt-4 font-mono text-xs md:text-sm text-white/65 leading-relaxed">
            Keep walking — the bar is methodology engagement, not gamified clicks. {ev.met_count} of {ev.total} criteria met so far.
          </p>
        )}
        {msg && (
          <div className="mt-3 font-mono text-xs text-[#2F8A92] border border-[#2F8A92]/25 bg-[#2F8A92]/5 rounded px-3 py-2" data-testid="cert-msg">
            {msg}
          </div>
        )}
      </div>

      {/* Badges */}
      <div data-testid="cert-badges">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={14} className="text-[#D4A84A]" />
          <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">Milestones · {badges.length}</span>
        </div>
        {badges.length === 0 ? (
          <p className="font-mono text-xs md:text-sm text-white/55">Walk a chain. Cast a review. Cite a source. Milestones accumulate as you go.</p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {badges.map((b) => (
              <li key={b.badge_key} className="border border-[#D4A84A]/15 rounded px-3 py-2.5" data-testid={`badge-${b.badge_key}`}>
                <div className="flex items-start gap-2.5">
                  <Trophy size={13} className="text-[#D4A84A] mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs md:text-sm text-white">{b.label}</div>
                    <div className="font-mono text-[10px] text-white/55 mt-0.5">{b.description}</div>
                    <div className="font-mono text-[10px] text-white/35 mt-0.5">
                      {new Date(b.awarded_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Era + Master Certificates panel */}
      <div className="pt-6 border-t border-white/10">
        <EraCertificates />
      </div>
    </div>
  );
}
